import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { FileIcon } from "components/files/file-icon";
import {
    fileTableColumns,
    fileTableInitialSort,
    FileTableRow,
} from "components/files/file-table";
import { CrmTable } from "components/table/crm-table";
import { defaultLinkStyle } from "components/ui/link";
import * as React from "react";
import { truncate } from "util/text";
import { SharedLeadLocation } from "./shared-pipeline-tabs";

export type SharedLeadFile = inferProcedureOutput<
    AppRouter["sharedPipeline"]["lead"]
>["files"][number];

interface DisplayableSharedFile extends FileTableRow {
    file: SharedLeadFile;
}

const sharedFileDownloadPath = (
    location: SharedLeadLocation,
    file: SharedLeadFile,
): string =>
    `/api/files/shared/${location.partnershipId}/${location.homeCareLeadId}/${file.id}?regionId=${location.regionId}`;

const toDisplayable = (file: SharedLeadFile): DisplayableSharedFile => ({
    file,
    originalFilename: file.originalFilename,
    sortableFilename: file.originalFilename.toLocaleLowerCase(),
    description: truncate(file.description ?? "", 60),
    createdAt: new Date(file.createdAt),
    displayUsername: file.uploadedByName,
    sortableUsername: file.uploadedByName.toLocaleLowerCase(),
});

const SharedFileLink: React.FC<{
    location: SharedLeadLocation;
    record: DisplayableSharedFile;
}> = ({ location, record }) => (
    <a
        href={sharedFileDownloadPath(location, record.file)}
        target="_blank"
        rel="noreferrer noopener"
        className={`${defaultLinkStyle} flex items-center space-x-2`}>
        <FileIcon mimeType={record.file.mimeType ?? ""} />
        <div>{record.file.originalFilename}</div>
        <ArrowTopRightOnSquareIcon aria-hidden="true" className="w-4 h-4" />
    </a>
);

const NoSharedFiles: React.FC = () => (
    <div className="text-gray-500 flex items-center space-x-1 mt-4">
        <FileIcon mimeType="pdf" />
        <div>No files shared yet.</div>
    </div>
);

export const SharedLeadFiles: React.FC<{
    location: SharedLeadLocation;
    files: SharedLeadFile[];
}> = ({ location, files }) => {
    const columns = React.useMemo(
        () =>
            fileTableColumns<DisplayableSharedFile>(record => (
                <SharedFileLink location={location} record={record} />
            )),
        [location],
    );
    const data = React.useMemo(() => files.map(toDisplayable), [files]);

    return (
        <div>
            <p className="text-lg">All files</p>
            {data.length === 0 ? (
                <NoSharedFiles />
            ) : (
                <div className="bg-white rounded border border-gray-200 mt-2">
                    <CrmTable
                        initialSort={fileTableInitialSort}
                        columns={columns}
                        data={data}
                    />
                </div>
            )}
        </div>
    );
};
